import type { BandwidthGroupBreakdownRow, BandwidthGroupUserRow } from './bandwidthDashboardTypes';
import { roundBwDecimal } from './bandwidthFormat';

/** Matches backend KPI rule: omit users with status inactive or active false when activity and hours are both zero. */
export function includeBandwidthKpiExportUser(user: {
  totalActivityCount: number;
  timeSpentHours: number;
  isInactiveUser?: boolean;
}): boolean {
  if (user.isInactiveUser !== true) return true;
  return user.totalActivityCount > 0 || user.timeSpentHours > 0;
}

function rollupGroupUserStats(users: BandwidthGroupUserRow[]): Pick<
  BandwidthGroupBreakdownRow,
  'totalActivityCount' | 'timeSpentHours' | 'avgHrsOnActivity'
> {
  const totalActivityCount = users.reduce((s, u) => s + u.totalActivityCount, 0);
  const timeSpentHours = roundBwDecimal(users.reduce((s, u) => s + u.timeSpentHours, 0));
  const avgHrsOnActivity =
    totalActivityCount > 0
      ? roundBwDecimal(timeSpentHours / totalActivityCount)
      : null;
  return { totalActivityCount, timeSpentHours, avgHrsOnActivity };
}

/** Teams KPI PDF/Excel: drop status-inactive zero-activity users and realign group totals. */
export function prepareBandwidthGroupSectionExport(
  groups: BandwidthGroupBreakdownRow[]
): BandwidthGroupBreakdownRow[] {
  return groups.map((g) => {
    const users = g.users.filter(includeBandwidthKpiExportUser);
    return { ...g, users, ...rollupGroupUserStats(users) };
  });
}

export type BandwidthCustomerSectionExportRow = {
  customer: string;
  customerId?: string;
  totalActivity: number;
  timeHrs: number;
  avgHrsOnActivity: number | null;
  users: Array<{
    userId?: string;
    userName: string;
    totalActivity: number;
    timeHrs: number;
    avgHrsOnActivity: number | null;
    isInactiveUser?: boolean;
  }>;
};

/** Customer KPI PDF/Excel: drop status-inactive zero-activity users and realign customer totals. */
export function prepareBandwidthCustomerSectionExport(
  rows: BandwidthCustomerSectionExportRow[]
): BandwidthCustomerSectionExportRow[] {
  return rows.map((r) => {
    const users = r.users.filter((u) =>
      includeBandwidthKpiExportUser({
        totalActivityCount: u.totalActivity,
        timeSpentHours: u.timeHrs,
        isInactiveUser: u.isInactiveUser,
      })
    );
    const totalActivity = users.reduce((s, u) => s + u.totalActivity, 0);
    const timeHrs = roundBwDecimal(users.reduce((s, u) => s + u.timeHrs, 0));
    const avgHrsOnActivity =
      totalActivity > 0 ? roundBwDecimal(timeHrs / totalActivity) : null;
    return { ...r, users, totalActivity, timeHrs, avgHrsOnActivity };
  });
}
